import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { deleteAllConnectedData } from '../api/client';
import { useAuditStore } from '../stores/useAuditStore';
import { useOnboardingStore } from '../stores/useOnboardingStore';

/**
 * The concrete form of the "Delete anytime" promise on the Connect step.
 *
 * Disconnects every platform and deletes everything collected from them. It is
 * irreversible and it is in the header on every screen, so it asks first —
 * and the dialog says plainly what goes and what stays, because "Delete data"
 * on its own could reasonably be read as deleting the account.
 */
export default function DeleteDataButton() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    if (deleting) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteAllConnectedData();
      // Wipe the local mirrors too — the grid caches connection status, and the
      // suggestion was inferred from data that no longer exists.
      useAuditStore.getState().reset();
      useOnboardingStore.getState().reset();
      try {
        sessionStorage.removeItem('analysis_chat_id');
        // The onboarding chat id too — left behind, a fresh /collect would
        // adopt the old chat, whose collection record says 'completed', and
        // the one-shot guard would refuse to re-read the sources the user
        // is about to reconnect.
        sessionStorage.removeItem('collect_chat_id');
      } catch { /* private mode */ }
      setOpen(false);
      navigate('/connect', { replace: true });
    } catch {
      setError("We couldn't delete your data. Try again in a moment.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex cursor-pointer items-center gap-1.5 rounded-md border border-destructive/25 px-2.5 py-1 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
      >
        <Trash2 className="size-3.5" />
        Delete data
      </button>

      <Dialog open={open} onOpenChange={(next) => !deleting && setOpen(next)}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Delete your connected data?</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              This disconnects every platform and deletes everything we collected
              from them. It can't be undone — you'd need to reconnect and run the
              audit again.
            </p>
            <p>
              Reports you've already generated stay, and so does the
              confidentiality agreement you signed.
            </p>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="mt-2 flex justify-end gap-2">
            <Button
              variant="outline"
              className="cursor-pointer"
              disabled={deleting}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="cursor-pointer"
              disabled={deleting}
              onClick={confirm}
            >
              {deleting ? 'Deleting…' : 'Delete data'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
